import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { soundNotifications } from '@/utils/soundNotifications';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useNotificationPreferences } from './useNotificationPreferences';

interface OrderNotification {
  type: 'order_placed' | 'order_ready' | 'order_assigned' | 'order_completed';
  orderId: string;
  orderNumber: string;
  message: string;
  data?: Record<string, unknown>;
}

interface SupabaseRealtimePayload {
  new?: Record<string, unknown>;
  old?: Record<string, unknown>;
}

export function useOrderNotifications() {
  const { user, userRoles, hasRole } = useAuth();
  const { preferences, shouldNotify, isInQuietHours } = useNotificationPreferences();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const processedNotifications = useRef<Set<string>>(new Set());

  const shouldReceiveNotification = useCallback((notificationType: string, data: Record<string, unknown>) => {
    if (!user || !userRoles) return false;

    switch (notificationType) {
      case 'order_placed':
        // CS, admin, super_admin, and dispatch receive new order notifications
        return hasRole('customer_service') || hasRole('admin') || hasRole('super_admin') || hasRole('dispatch');
      
      case 'order_ready':
        // CS, admin, super_admin, and dispatch receive ready notifications
        return hasRole('customer_service') || hasRole('admin') || hasRole('super_admin') || hasRole('dispatch');
      
      case 'order_assigned':
        // Only the assigned rider receives this notification
        return hasRole('rider') && data.assigned_rider_id === user.id;
      
      case 'order_completed':
        // CS and dispatch receive completion notifications
        return hasRole('customer_service') || hasRole('dispatch') || hasRole('admin') || hasRole('super_admin');
      
      default:
        return false;
    }
  }, [user, userRoles, hasRole]);

  const showNotification = useCallback(async (notification: OrderNotification) => {
    // Check if in quiet hours
    if (isInQuietHours) {
      console.log('Notification suppressed: quiet hours active');
      return;
    }

    // Check preferences for this notification type
    const notificationTypeMap: Record<string, keyof typeof preferences> = {
      'order_placed': 'notify_order_placed',
      'order_ready': 'notify_order_ready',
      'order_assigned': 'notify_order_assigned',
      'order_completed': 'notify_order_completed',
    };

    const prefKey = notificationTypeMap[notification.type];
    if (prefKey && !shouldNotify(prefKey)) {
      console.log(`Notification suppressed: ${prefKey} disabled in preferences`);
      return;
    }

    // Create unique key to prevent duplicate notifications
    const notificationKey = `${notification.type}-${notification.orderId}-${Date.now()}`;
    
    if (processedNotifications.current.has(notificationKey)) {
      return;
    }
    
    processedNotifications.current.add(notificationKey);
    
    // Clean old notifications after 1 minute
    setTimeout(() => {
      processedNotifications.current.delete(notificationKey);
    }, 60000);

    // Play sound if enabled
    if (preferences?.sound_enabled) {
      try {
        await soundNotifications.playNewOrderNotification();
      } catch (error) {
        console.error('Failed to play notification sound:', error);
      }
    }

    // Show in-app toast notification if enabled
    if (preferences?.in_app_enabled) {
      const toastOptions = {
        duration: 5000,
        action: {
          label: 'View',
          onClick: () => {
            window.location.href = `/admin/orders/${notification.orderId}`;
          }
        }
      };

      switch (notification.type) {
        case 'order_placed':
          toast.success(`🆕 New Order: ${notification.orderNumber}`, toastOptions);
          break;
        case 'order_ready':
          toast.info(`✅ Order Ready: ${notification.orderNumber}`, toastOptions);
          break;
        case 'order_assigned':
          toast.info(`🚚 New Delivery: ${notification.orderNumber}`, toastOptions);
          break;
        case 'order_completed':
          toast.success(`🎉 Order Delivered: ${notification.orderNumber}`, toastOptions);
          break;
      }
    }

    // Browser notification (if permission granted and push enabled)
    if (preferences?.push_enabled && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const browserNotification = new Notification(`Dough Man Foods - ${notification.message}`, {
          body: `Order #${notification.orderNumber}`,
          icon: '/pwa-192x192.png',
          badge: '/pwa-64x64.png',
          tag: notificationKey,
          requireInteraction: false,
        });

        browserNotification.onclick = () => {
          window.focus();
          window.location.href = `/admin/orders/${notification.orderId}`;
          browserNotification.close();
        };
      } catch (error) {
        console.error('Failed to show browser notification:', error);
      }
    }
  }, [preferences, shouldNotify, isInQuietHours]);

  const handleOrderInsert = useCallback((payload: SupabaseRealtimePayload) => {
    const order = payload.new;
    
    if (shouldReceiveNotification('order_placed', order)) {
      showNotification({
        type: 'order_placed',
        orderId: order.id,
        orderNumber: order.order_number,
        message: 'New Order Placed',
        data: order
      });
    }
  }, [shouldReceiveNotification, showNotification]);

  const handleOrderUpdate = useCallback((payload: SupabaseRealtimePayload) => {
    const oldOrder = payload.old;
    const newOrder = payload.new;

    // Check for status changes
    if (oldOrder.status !== newOrder.status) {
      // Order marked as ready for pickup
      if (newOrder.status === 'ready_for_pickup' && shouldReceiveNotification('order_ready', newOrder)) {
        showNotification({
          type: 'order_ready',
          orderId: newOrder.id,
          orderNumber: newOrder.order_number,
          message: 'Order Ready for Pickup',
          data: newOrder
        });
      }

      // Order completed/delivered
      if (newOrder.status === 'delivered' && shouldReceiveNotification('order_completed', newOrder)) {
        showNotification({
          type: 'order_completed',
          orderId: newOrder.id,
          orderNumber: newOrder.order_number,
          message: 'Order Completed',
          data: newOrder
        });
      }
    }

    // Check if order was assigned to a rider
    if (!oldOrder.assigned_to && newOrder.assigned_to) {
      if (shouldReceiveNotification('order_assigned', { 
        ...newOrder, 
        assigned_rider_id: newOrder.assigned_to 
      })) {
        showNotification({
          type: 'order_assigned',
          orderId: newOrder.id,
          orderNumber: newOrder.order_number,
          message: 'New Delivery Assignment',
          data: newOrder
        });
      }
    }
  }, [shouldReceiveNotification, showNotification]);

  useEffect(() => {
    if (!user || !userRoles) return;

    // Enable sound notifications
    soundNotifications.setEnabled(true);
    soundNotifications.requestPermission().catch(() => {});

    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }

    // Subscribe to order changes via Realtime
    const channel = supabase
      .channel('order-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders'
        },
        handleOrderInsert
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders'
        },
        handleOrderUpdate
      )
      .subscribe((status) => {
        console.log('Order notifications subscription status:', status);
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    }
  }, [user, userRoles, handleOrderInsert, handleOrderUpdate]);

  return {
    enabled: !!user && !!userRoles,
  };
}
